import React from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDate, formatTime } from '@/lib/dateUtils';
import { Button } from '@/components/ui/button';
import {
  AlertCircle, ShieldCheck, Star, CheckCircle2, CalendarDays, MapPin, Video, User, ChevronRight,
  Briefcase, Info,
} from 'lucide-react';
import api from '@/lib/api';
import { refreshInternshipAlerts } from '@/lib/useInternshipAlerts';
import { isUpcoming, todayLocal, bookingMode, modeLabel } from '@/lib/experienceStatus';

export function SectionHeading({ title, action, to, testId }) {
  const navigate = useNavigate();
  return (
    <div className="flex items-center justify-between gap-4 mb-4">
      <h2 className="text-base md:text-lg font-bold text-[#1F2937] font-['Outfit'] tracking-tight">{title}</h2>
      {action && (
        <button onClick={() => navigate(to)} className="text-xs font-semibold text-[#F97D6A] hover:text-[#F06651] flex items-center gap-0.5" data-testid={testId}>
          {action} <ChevronRight className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

/** Aggregated parent actions across every linked child. */
export function buildParentActions(children, bookings, internshipAlerts) {
  const actions = [];

  (internshipAlerts || []).forEach(a => {
    if (a.kind === 'applications_open') {
      actions.push({
        key: a.key, priority: 1, date: a.deadline, childName: a.child_name, icon: Briefcase,
        title: `${a.program_name} — applications open`,
        subtitle: `Apply by ${formatDate(a.deadline)}`,
        cta: 'Explore & Apply', to: `/parent/internships?child=${a.child_id}&program=${a.program_id}`,
      });
    } else if (a.kind === 'confirmation_pending') {
      actions.push({
        key: a.key, priority: 0, date: a.start_date, childName: a.child_name, icon: Briefcase,
        title: `${a.title} — confirm participation`,
        subtitle: `${a.company_name}${a.start_date ? ` · starts ${formatDate(a.start_date)}` : ''}`,
        cta: 'Confirm Participation',
        to: `/parent/internships?child=${a.child_id}&program=${a.program_id}&confirm=${a.application_id}`,
      });
    } else if (a.kind === 'instructions_new') {
      actions.push({
        key: a.key, priority: 3, date: a.start_date, childName: a.child_name, icon: Info,
        informational: true,
        title: `Joining instructions are ready for ${a.title}`,
        subtitle: `${a.company_name} has shared where and when to report`,
        cta: 'View Instructions', to: `/parent/internships?child=${a.child_id}&program=${a.program_id}`,
        seenId: a.application_id,
      });
    }
  });

  (children || []).forEach(c => {
    if (c.onboarding_status === 'awaiting_consent') {
      actions.push({
        key: `consent-${c.id}`, priority: 0, childName: c.full_name,
        title: 'Parental consent required',
        subtitle: 'Approve participation so your child can book experiences',
        cta: 'Give Consent', to: '/parent/consent', icon: ShieldCheck,
      });
    }
  });

  (bookings || []).forEach(b => {
    if (b.status === 'pending_parent_confirmation') {
      actions.push({
        key: `confirm-${b.id}`, priority: 1, date: b.assigned_date, childName: b.child_name,
        title: `${b.career_name} — accept or decline`,
        subtitle: `${b.mentor_name ? `${b.mentor_name} · ` : ''}${b.assigned_date ? formatDate(b.assigned_date) : 'Date to be scheduled'}${b.assigned_time_slot ? ` · ${formatTime(b.assigned_time_slot)}` : ''}`,
        cta: 'Review', to: `/sessions/${b.id}`, icon: AlertCircle,
      });
    } else if (b.status === 'awaiting_feedback') {
      actions.push({
        key: `feedback-${b.id}`, priority: 2, date: b.assigned_date, childName: b.child_name,
        title: `${b.career_name} — share your feedback`,
        subtitle: `Completed${b.assigned_date ? ` on ${formatDate(b.assigned_date)}` : ''}${b.mentor_name ? ` with ${b.mentor_name}` : ''}`,
        cta: 'Give Feedback', to: `/sessions/${b.id}?feedback=1`, icon: Star,
      });
    }
  });

  return actions.sort((a, b) =>
    a.priority - b.priority || String(a.date || '9999').localeCompare(String(b.date || '9999')));
}

export function NeedsAttention({ children, bookings, internshipAlerts }) {
  const navigate = useNavigate();
  const actions = buildParentActions(children, bookings, internshipAlerts);
  const shown = actions.slice(0, 5);

  const open = async (a) => {
    if (a.seenId) {
      try { await api.post(`/parent/internship-placements/${a.seenId}/instructions-seen`); } catch { /* the page still opens */ }
      refreshInternshipAlerts();
    }
    navigate(a.to);
  };

  return (
    <section className="mb-10" data-testid="needs-attention">
      <SectionHeading
        title="Needs your attention"
        action={actions.length > shown.length ? `View all (${actions.length})` : null}
        to="/parent/experiences"
        testId="view-all-actions"
      />
      {actions.length === 0 ? (
        <div className="rounded-2xl bg-[#F6FBF8] border border-[#DCF0E5] px-5 py-4 flex items-center gap-3" data-testid="no-actions">
          <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0" />
          <p className="text-sm text-emerald-800">You're all caught up — nothing needs your attention right now.</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {shown.map(a => (
            <div key={a.key} className={`rounded-2xl border p-4 flex flex-col sm:flex-row sm:items-center gap-3 ${a.informational ? 'border-[#E5E7EB] bg-[#FAFBFC]' : 'border-[#FBD9D2] bg-[#FFF7F5]'}`} data-testid={`action-${a.key}`}>
              <a.icon className={`h-5 w-5 shrink-0 ${a.informational ? 'text-[#6B7280]' : 'text-[#F97D6A]'}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-semibold text-[#1F2937]">{a.title}</p>
                  <span className={`text-[10px] font-semibold bg-white rounded-full px-2 py-0.5 shrink-0 ${a.informational ? 'text-[#6B7280]' : 'text-[#F97D6A]'}`} data-testid={`action-child-${a.key}`}>
                    {a.childName}
                  </span>
                </div>
                <p className="text-sm text-[#6B7280] mt-0.5 break-words line-clamp-2 sm:truncate">{a.subtitle}</p>
              </div>
              <Button
                size="sm"
                variant={a.informational ? 'outline' : 'default'}
                onClick={() => open(a)}
                className={`rounded-full h-11 sm:h-8 w-full sm:w-auto px-3.5 text-xs font-semibold shrink-0 ${a.informational ? 'border-[#E5E7EB] text-[#1F2937]' : 'bg-[#F97D6A] text-white hover:bg-[#F06651]'}`}
                data-testid={`action-cta-${a.key}`}
              >
                {a.cta}
              </Button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export function UpcomingExperiences({ bookings }) {
  const navigate = useNavigate();
  const today = todayLocal();
  const upcoming = (bookings || [])
    .filter(b => isUpcoming(b, today))
    .sort((a, b) => (a.assigned_date || '').localeCompare(b.assigned_date || '')
      || (a.assigned_time_slot || '').localeCompare(b.assigned_time_slot || ''));
  const shown = upcoming.slice(0, 3);

  return (
    <section className="mb-10" data-testid="upcoming-experiences">
      <SectionHeading
        title="Upcoming experiences"
        action={upcoming.length ? 'View all' : null}
        to="/parent/experiences"
        testId="view-all-experiences"
      />
      {shown.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#E5E7EB] p-8 text-center" data-testid="upcoming-empty">
          <CalendarDays className="h-6 w-6 text-gray-200 mx-auto mb-2" />
          <p className="text-sm text-[#9CA3AF]">No confirmed experiences scheduled yet.</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {shown.map(b => {
            const mode = bookingMode(b);
            return (
              <div
                key={b.id}
                onClick={() => navigate(`/sessions/${b.id}`)}
                className="rounded-2xl border border-[#EFF0F2] bg-white p-4 cursor-pointer transition-all hover:border-[#E0E2E6] hover:shadow-[0_2px_14px_rgba(16,24,40,0.06)]"
                data-testid={`upcoming-${b.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-[#1F2937] font-['Outfit'] truncate">{b.career_name}</p>
                    <div className="flex items-center gap-3 flex-wrap text-sm text-[#6B7280] mt-1.5">
                      <span className="flex items-center gap-1.5" data-testid={`upcoming-child-${b.id}`}>
                        <User className="h-3.5 w-3.5 text-[#9CA3AF]" />{b.child_name}
                      </span>
                      <span className="flex items-center gap-1.5">
                        {mode === 'Offline' ? <MapPin className="h-3.5 w-3.5 text-[#9CA3AF]" /> : <Video className="h-3.5 w-3.5 text-[#9CA3AF]" />}
                        {modeLabel(b)}
                      </span>
                      {b.mentor_name && <span className="truncate">{b.mentor_name}</span>}
                    </div>
                    <p className="text-xs text-[#9CA3AF] mt-1">
                      {formatDate(b.assigned_date)}{b.assigned_time_slot ? ` · ${formatTime(b.assigned_time_slot)}` : ''}
                    </p>
                  </div>
                  <Button variant="outline" size="sm" className="rounded-full h-8 px-3.5 text-xs border-[#E5E7EB] shrink-0" data-testid={`upcoming-details-${b.id}`}>
                    View Details
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

export function ContinueExploring() {
  const navigate = useNavigate();
  return (
    <section data-testid="continue-exploring">
      <SectionHeading title="Continue exploring" />
      <div className="grid sm:grid-cols-2 gap-3">
        <button
          onClick={() => navigate('/careers')}
          className="text-left rounded-2xl border border-[#EFF0F2] bg-white p-4 transition-all hover:border-[#E0E2E6] hover:shadow-[0_2px_14px_rgba(16,24,40,0.06)]"
          data-testid="explore-careers-card"
        >
          <p className="text-sm font-bold text-[#1F2937] font-['Outfit']">Explore Careers</p>
          <p className="text-sm text-[#6B7280] mt-1">Browse the career library and request an experience with a school-approved mentor.</p>
          <span className="text-xs font-semibold text-[#F97D6A] mt-3 inline-flex items-center gap-1">Browse <ChevronRight className="h-3.5 w-3.5" /></span>
        </button>
        <button
          onClick={() => navigate('/careers?openBuddy=1')}
          className="text-left rounded-2xl border border-[#F97D6A]/15 bg-gradient-to-br from-[#FFF7F5] to-[#FFF0ED] p-4 transition-all hover:shadow-[0_2px_14px_rgba(249,125,106,0.12)]"
          data-testid="career-buddy-card"
        >
          <p className="text-sm font-bold text-[#1F2937] font-['Outfit']">Career Buddy</p>
          <p className="text-sm text-[#6B7280] mt-1">Answer a few questions together and discover careers that match your child.</p>
          <span className="text-xs font-semibold text-[#F97D6A] mt-3 inline-flex items-center gap-1">Try it <ChevronRight className="h-3.5 w-3.5" /></span>
        </button>
      </div>
    </section>
  );
}
